/**
 * JobHistoryModal - Modal showing run history for a scheduled job
 */
import { X } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { Dialog, DialogTrigger, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { useJobHistory } from "@/hooks/api/use-scheduler-jobs";
import type { JobRun } from "@/types/api/settings";
import { cx } from "@/utils/cx";

interface JobHistoryModalProps {
    jobId: string;
    jobName: string;
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Format duration in ms to human readable
 */
function formatDuration(ms?: number): string {
    if (!ms) return "-";
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
}

/**
 * Format date to locale string
 */
function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString();
}

/**
 * Status badge for job run
 */
function RunStatusBadge({ status }: { status: JobRun["status"] }) {
    switch (status) {
        case "running":
            return (
                <Badge color="blue" size="sm">
                    Running
                </Badge>
            );
        case "success":
            return (
                <Badge color="success" size="sm">
                    Success
                </Badge>
            );
        case "failed":
            return (
                <Badge color="error" size="sm">
                    Failed
                </Badge>
            );
        default:
            return (
                <Badge color="gray" size="sm">
                    Unknown
                </Badge>
            );
    }
}

export function JobHistoryModal({ jobId, jobName, isOpen, onClose }: JobHistoryModalProps) {
    const { data: history, isLoading, error } = useJobHistory(jobId, 50);

    return (
        <DialogTrigger isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <ModalOverlay isDismissable>
                <Modal className="max-w-3xl">
                    <Dialog>
                        <div className="w-full rounded-xl bg-primary shadow-xl ring-1 ring-secondary">
                            {/* Header */}
                            <div className="flex items-center justify-between border-b border-secondary px-6 py-4">
                                <div>
                                    <h2 className="text-lg font-semibold text-primary">Job History</h2>
                                    <p className="text-sm text-tertiary">{jobName}</p>
                                </div>
                                <Button
                                    color="tertiary"
                                    size="sm"
                                    iconLeading={X}
                                    onClick={onClose}
                                    aria-label="Close"
                                />
                            </div>

                            {/* Content */}
                            <div className="p-6 max-h-[60vh] overflow-y-auto">
                                {isLoading && (
                                    <div className="flex items-center justify-center py-8 text-tertiary">
                                        Loading history...
                                    </div>
                                )}

                                {error && (
                                    <div className="flex items-center justify-center py-8 text-error-primary">
                                        Failed to load history
                                    </div>
                                )}

                                {!isLoading && !error && (!history || history.length === 0) && (
                                    <div className="flex items-center justify-center py-8 text-tertiary">
                                        No run history available
                                    </div>
                                )}

                                {!isLoading && !error && history && history.length > 0 && (
                                    <div className="space-y-3">
                                        {history.map((run) => (
                                            <div
                                                key={run.run_id}
                                                className={cx(
                                                    "rounded-lg border p-4",
                                                    run.status === "failed"
                                                        ? "border-error-primary/20 bg-error-primary/5"
                                                        : "border-secondary bg-secondary/50"
                                                )}
                                            >
                                                <div className="flex items-start justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <RunStatusBadge status={run.status} />
                                                        <span className="text-sm font-mono text-tertiary">
                                                            {formatDate(run.started_at)}
                                                        </span>
                                                    </div>
                                                    <span className="text-sm text-tertiary">
                                                        {formatDuration(run.duration_ms)}
                                                    </span>
                                                </div>

                                                {/* Stats */}
                                                <div className="flex gap-4 text-sm text-tertiary mb-2">
                                                    <span>
                                                        Processed:{" "}
                                                        <span className="text-primary font-medium">
                                                            {run.items_processed}
                                                        </span>
                                                    </span>
                                                    <span>
                                                        Succeeded:{" "}
                                                        <span className="text-success-primary font-medium">
                                                            {run.items_succeeded}
                                                        </span>
                                                    </span>
                                                    <span>
                                                        Failed:{" "}
                                                        <span className="text-error-primary font-medium">
                                                            {run.items_failed}
                                                        </span>
                                                    </span>
                                                </div>

                                                {/* Error message */}
                                                {run.error_message && (
                                                    <div className="mt-2 p-2 rounded bg-error-primary/10 text-sm text-error-primary font-mono whitespace-pre-wrap">
                                                        {run.error_message}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-end border-t border-secondary px-6 py-4">
                                <Button color="secondary" onClick={onClose}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </DialogTrigger>
    );
}
